/**
 * TanStack Query plumbing for the capability cache.
 *
 * Capabilities are cached and refreshed through a single TanStack Query
 * `QueryClient` rather than the bespoke `FlagStore` the flags track still uses.
 * The client is a `globalThis` singleton — mirroring `setDefaultFlagStore` and
 * the in-flight registry in {@link ./cache} / {@link ./web-storage-store} — so
 * every `CapabilitiesProvider` shares one in-memory cache and one persisted
 * snapshot across separately-mounted subtrees, and (when react-query is deduped
 * to a single copy) across micro-frontends.
 *
 * Persistence uses `@tanstack/query-sync-storage-persister` against localStorage.
 * `maxAge` is the hard **cache-expiry**: a persisted snapshot older than `maxAge`
 * is dropped on restore instead of being served.
 */
import {
  QueryClient,
  defaultShouldDehydrateQuery,
  type QueryKey,
} from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { persistQueryClient } from "@tanstack/react-query-persist-client";

import type { WireEvaluationContext } from "./wire";

/** Bump to invalidate every persisted capability snapshot after a shape change. */
const SCHEMA_VERSION = "3";

/**
 * Every key produced by {@link capabilitiesQueryKey} starts with this segment.
 * The persister uses it to dehydrate capability queries only — if a host app
 * ever runs its own queries against this client (e.g. by mounting its hooks
 * under the provider's `QueryClientProvider`), its data must not leak into the
 * localStorage snapshot, where it would survive reloads and workspace switches.
 */
const QUERY_KEY_PREFIX = "emil-flags";

/** localStorage key the persister writes the dehydrated client under. */
const PERSIST_KEY = "emil-flags:caps";

/** `globalThis` slot, so separately-bundled SDK copies share one client. */
const GLOBAL_KEY = "__emilFlagsCapabilitiesQueryClient__";

/** `globalThis` slot for the one-time cache-restore promise (see below). */
const RESTORE_KEY = "__emilFlagsCapabilitiesRestored__";

/** 60s — matches the previous `FlagStore` TTL. */
const DEFAULT_STALE_TIME_MS = 60_000;
/** 24h; kept `>=` maxAge so persisted entries aren't GC'd from memory early. */
const DEFAULT_CACHE_TIME_MS = 24 * 60 * 60 * 1000;
/** 24h hard cache-expiry: older persisted snapshots are dropped on restore. */
const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface CapabilitiesQueryClientOptions {
  /** Time (ms) a cached snapshot is served before it's considered stale. Default 60s. */
  staleTime?: number;
  /** Time (ms) an unused snapshot stays in memory. Default 24h; kept `>=` maxAge. */
  cacheTime?: number;
  /**
   * Hard cache-expiry (ms): persisted snapshots older than this are dropped on
   * restore rather than served. Default 24h.
   */
  maxAge?: number;
  /**
   * Storage backend for persistence. Defaults to `localStorage`; pass a custom
   * `Storage` (e.g. in tests) or `null` to disable persistence entirely.
   */
  storage?: Storage | null;
}

interface ClientHost {
  [GLOBAL_KEY]?: QueryClient;
  [RESTORE_KEY]?: Promise<unknown>;
}

function resolveStorage(explicit?: Storage | null): Storage | undefined {
  // `null` explicitly disables persistence; `undefined` falls back to localStorage.
  if (explicit !== undefined) return explicit ?? undefined;
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    // Accessing localStorage can throw in sandboxed iframes / blocked storage.
  }
  return undefined;
}

/**
 * Get the process-wide capability `QueryClient`, creating it and wiring up
 * localStorage persistence on first use. `options` apply only to that first
 * creation (persistence is process-wide); later callers get the existing
 * singleton. Vary freshness per provider by passing `staleTime`/`cacheTime` to
 * the individual `useQuery` instead.
 */
export function getCapabilitiesQueryClient(
  options: CapabilitiesQueryClientOptions = {},
): QueryClient {
  const host = globalThis as ClientHost;
  const existing = host[GLOBAL_KEY];
  if (existing) return existing;

  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: options.staleTime ?? DEFAULT_STALE_TIME_MS,
        cacheTime: options.cacheTime ?? DEFAULT_CACHE_TIME_MS,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });

  const storage = resolveStorage(options.storage);
  if (storage) {
    // `persistQueryClient` restores the cache **asynchronously** (and then
    // subscribes to persist future changes). We keep the restore promise so
    // capability queries can wait for hydration before fetching — otherwise the
    // first query races the restore and always hits the network even when a
    // fresh snapshot is in localStorage. See `whenCapabilitiesRestored`.
    const [, restored] = persistQueryClient({
      queryClient: client,
      persister: createSyncStoragePersister({ storage, key: PERSIST_KEY }),
      maxAge: options.maxAge ?? DEFAULT_MAX_AGE_MS,
      // Invalidate the whole persisted cache when the snapshot shape changes.
      buster: SCHEMA_VERSION,
      dehydrateOptions: {
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) &&
          query.queryKey[0] === QUERY_KEY_PREFIX,
      },
    });
    host[RESTORE_KEY] = restored;
  } else {
    // No persistence — nothing to wait for.
    host[RESTORE_KEY] = Promise.resolve();
  }

  host[GLOBAL_KEY] = client;
  return client;
}

/**
 * Resolves once the persisted capability cache has been read back into the
 * singleton client — or immediately when persistence is unavailable. Restoration
 * is asynchronous, so a capability query must await this before it fetches;
 * fetching first would race the restore and always hit the network even when a
 * fresh snapshot exists in localStorage. Returns a resolved promise if no client
 * has been created yet.
 */
export function whenCapabilitiesRestored(): Promise<unknown> {
  return (globalThis as ClientHost)[RESTORE_KEY] ?? Promise.resolve();
}

/**
 * The query key a capability snapshot is cached under. Encodes the environment,
 * the evaluation context, and — when supplied — the tenant, so one user never
 * reads another's resolved capabilities from a shared client (the same scoping
 * intent as `scopeOf` in {@link ./cache}).
 *
 * `tenantId` is appended only when given, so existing keys (and their persisted
 * snapshots) are unchanged for callers that don't scope by tenant. The cache is
 * otherwise blind to the bearer token, so passing a tenant id is what keeps two
 * tenants on the same origin from colliding — see `CapabilitiesProvider`.
 */
export function capabilitiesQueryKey(
  envId: string,
  context?: WireEvaluationContext,
  tenantId?: string,
): QueryKey {
  const key: Array<string> = [
    QUERY_KEY_PREFIX,
    "capabilities",
    envId,
    context?.key ?? "",
    context?.attributes ?? "",
  ];
  if (tenantId) {
    key.push(tenantId);
  }
  return key;
}

/**
 * Drop the cached capabilities and the persisted snapshot — e.g. on logout or a
 * tenant switch. Clears the existing singleton's cache (so mounted providers
 * keep working against the same client) and removes the persisted entry. Safe to
 * call before any client has been created.
 */
export function resetCapabilitiesQueryClient(): void {
  (globalThis as ClientHost)[GLOBAL_KEY]?.clear();
  try {
    resolveStorage()?.removeItem(PERSIST_KEY);
  } catch {
    /* ignore */
  }
}
